import React, { useState } from 'react';
import {
  Card,
  Button,
  Form,
  Input,
  Space,
  Typography,
  List,
  Spin,
  App,
  Tag,
  Popconfirm,
  Modal,
  InputNumber,
  Checkbox,
  Progress,
  Table,
  Empty,
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, CopyOutlined, PlusOutlined, SearchOutlined, RedoOutlined, DownloadOutlined, HistoryOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { spamLinkApi, websiteApi } from '../services/api';
import { cloudflareApi } from '../services/cloudflareApi';
import { SpamLinkDomain, AddDomainsDto, SpamLinkDomainHistory } from '../types/spam-link';
import { Website } from '../types/website';

const { Title } = Typography;

const SpamLinkDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message: messageApi } = App.useApp();
  const [form] = Form.useForm();
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [availableDomains, setAvailableDomains] = useState<Website[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [savedDomains, setSavedDomains] = useState<SpamLinkDomain[]>([]);
  const [loadingSavedDomains, setLoadingSavedDomains] = useState(false);
  const [savingDomains, setSavingDomains] = useState(false);
  const [domainZoneIds, setDomainZoneIds] = useState<Record<string, string>>({});
  const [loadMoreModalVisible, setLoadMoreModalVisible] = useState(false);
  const [loadMoreForm] = Form.useForm();
  const [removeModalVisible, setRemoveModalVisible] = useState(false);
  const [removeModalForm] = Form.useForm();
  const [domainToRemove, setDomainToRemove] = useState<{ domain: SpamLinkDomain; index: number } | null>(null);
  const [loadMoreDomains, setLoadMoreDomains] = useState<Website[]>([]);
  const [loadingLoadMore, setLoadingLoadMore] = useState(false);
  const [selectedLoadMoreDomains, setSelectedLoadMoreDomains] = useState<string[]>([]);
  const [redirectProgress, setRedirectProgress] = useState<{
    visible: boolean;
    current: number;
    total: number;
    currentDomain: string;
  }>({
    visible: false,
    current: 0,
    total: 0,
    currentDomain: '',
  });
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<Website[]>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);

  const { data: spamLink, isLoading } = useQuery(
    ['spam-link', id],
    () => spamLinkApi.getById(id!),
    {
      enabled: !!id,
      onSuccess: (data) => {
        form.setFieldsValue({
          domain_url: data.domain_url,
        });
        // Load saved domains
        loadSavedDomains();
      },
    }
  );

  // Fetch domain history
  const { data: domainHistory, isLoading: loadingHistory, refetch: refetchHistory } = useQuery(
    ['spam-link-history', id],
    () => spamLinkApi.getDomainHistory(id!),
    {
      enabled: !!id,
    }
  );

  const loadSavedDomains = async () => {
    if (!id) return;
    setLoadingSavedDomains(true);
    try {
      const domains = await spamLinkApi.getDomains(id);
      setSavedDomains(domains || []);
      
      // Fetch zone IDs for domains that have website_id
      const zoneIdMap: Record<string, string> = {};
      const websiteIds = domains
        .filter(d => d.website_id)
        .map(d => d.website_id!)
        .filter((id, index, self) => self.indexOf(id) === index); // unique IDs
      
      if (websiteIds.length > 0) {
        try {
          const websites = await websiteApi.getAll({ 
            page: 1, 
            limit: 1000,
            search: '' 
          });
          
          websites.data.forEach((website: any) => {
            if (website.cloudflare_zone_id) {
              // Find domains that belong to this website
              domains.forEach(domain => {
                if (domain.website_id === website.id) {
                  zoneIdMap[domain.domain] = website.cloudflare_zone_id;
                }
              });
            }
          });
        } catch (error) {
          console.error('Error fetching website zone IDs:', error);
        }
      }
      
      setDomainZoneIds(zoneIdMap);
    } catch (error: any) {
      console.error('Error loading saved domains:', error);
      messageApi.error('Failed to load saved domains');
    } finally {
      setLoadingSavedDomains(false);
    }
  };

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: { domain_url: string } }) =>
      spamLinkApi.update(id, data),
    {
      onSuccess: () => {
        messageApi.success('Spam link updated successfully!');
        queryClient.invalidateQueries(['spam-link', id]);
        queryClient.invalidateQueries('spam-links');
      },
      onError: (error: any) => {
        messageApi.error(error.response?.data?.message || 'Failed to update spam link');
      },
    }
  );

  const handleGetAvailableDomains = async () => {
    if (!spamLink) {
      messageApi.warning('Spam link not loaded');
      return;
    }

    setLoadingDomains(true);
    try {
      // Fetch websites with status = 'active'
      // Limit to target_domains_count
      const targetCount = spamLink.target_domains_count || 10;
      
      const result = await websiteApi.getAll({ 
        page: 1, 
        limit: targetCount,
        status: 'active',
        search: ''
      });
      
      const domains = result.data || [];
      setAvailableDomains(domains);
      messageApi.success(`Found ${domains.length} available domains (status: active)`);
    } catch (error: any) {
      messageApi.error('Failed to load available domains');
      console.error('Error loading domains:', error);
    } finally {
      setLoadingDomains(false);
    }
  };

  const handleSearchDomains = async () => {
    if (!searchText.trim()) {
      messageApi.warning('Please enter a search term');
      return;
    }

    setLoadingSearch(true);
    try {
      const result = await websiteApi.getAll({ 
        page: 1, 
        limit: 50,
        status: 'active',
        search: searchText.trim()
      });
      
      const domains = result.data || [];
      
      // Filter out already saved domains and already in availableDomains
      const savedDomainIds = new Set(savedDomains.map(d => d.website_id).filter(Boolean));
      const availableDomainIds = new Set(availableDomains.map(d => d.id));
      const filteredDomains = domains.filter(website => 
        !savedDomainIds.has(website.id) && !availableDomainIds.has(website.id)
      );
      
      setSearchResults(filteredDomains);
      if (filteredDomains.length === 0) {
        messageApi.info('No domains found matching your search');
      } else {
        messageApi.success(`Found ${filteredDomains.length} domain(s) matching "${searchText}"`);
      }
    } catch (error: any) {
      messageApi.error('Failed to search domains');
      console.error('Error searching domains:', error);
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleAddDomainFromSearch = (website: Website) => {
    // Check if already in availableDomains
    if (availableDomains.some(d => d.id === website.id)) {
      messageApi.warning(`Domain "${website.domain}" is already in Available Domains`);
      return;
    }

    // Check if already saved
    if (savedDomains.some(d => d.website_id === website.id && d.domain === website.domain)) {
      messageApi.warning(`Domain "${website.domain}" is already saved`);
      return;
    }

    // Add to availableDomains
    setAvailableDomains([...availableDomains, website]);
    messageApi.success(`Domain "${website.domain}" added to Available Domains`);
    
    // Remove from search results (keep search text and other results)
    setSearchResults(searchResults.filter(d => d.id !== website.id));
  };

  const handleRebuildRedirectRules = async () => {
    if (!spamLink || !spamLink.domain_url || savedDomains.length === 0) {
      messageApi.warning('No domains to rebuild redirect rules for');
      return;
    }

    // Show progress modal
    setRedirectProgress({
      visible: true,
      current: 0,
      total: savedDomains.length,
      currentDomain: '',
    });

    const rebuildResults: Array<{ success: boolean; domain: string; error?: any }> = [];

    // Process sequentially to update progress
    for (let i = 0; i < savedDomains.length; i++) {
      const domain = savedDomains[i];
      
      // Get zoneId for this domain
      let zoneId = domainZoneIds[domain.domain];
      
      // If zoneId not found in cache, try to fetch from website
      if (!zoneId && domain.website_id) {
        try {
          const website = await websiteApi.getById(domain.website_id);
          if (website?.cloudflare_zone_id) {
            zoneId = website.cloudflare_zone_id;
          }
        } catch (error) {
          console.error(`Failed to fetch website for zoneId (${domain.domain}):`, error);
        }
      }

      setRedirectProgress({
        visible: true,
        current: i,
        total: savedDomains.length,
        currentDomain: domain.domain,
      });

      try {
        console.log(`Rebuilding redirect rule for ${domain.domain} to ${spamLink.domain_url}`);
        await cloudflareApi.createRedirectRule(
          domain.domain,
          spamLink.domain_url,
          zoneId
        );
        console.log(`Successfully rebuilt redirect rule for ${domain.domain}`);
        rebuildResults.push({ success: true, domain: domain.domain });
      } catch (error: any) {
        console.error(`Failed to rebuild redirect rule for ${domain.domain}:`, error);
        console.error('Error details:', error.response?.data || error.message);
        rebuildResults.push({ success: false, domain: domain.domain, error });
      }
    }

    // Update final progress
    setRedirectProgress({
      visible: true,
      current: savedDomains.length,
      total: savedDomains.length,
      currentDomain: '',
    });

    const rebuildSuccessCount = rebuildResults.filter(r => r.success).length;
    const rebuildFailedCount = rebuildResults.filter(r => !r.success).length;

    // Close progress modal after a short delay
    setTimeout(() => {
      setRedirectProgress({
        visible: false,
        current: 0,
        total: 0,
        currentDomain: '',
      });

      if (rebuildFailedCount > 0) {
        console.warn(`${rebuildFailedCount} domain(s) failed to rebuild redirect rule`);
        messageApi.warning(`${rebuildSuccessCount} redirect rule(s) rebuilt, ${rebuildFailedCount} failed`);
      } else {
        messageApi.success(`Successfully rebuilt ${rebuildSuccessCount} redirect rule(s)`);
      }
    }, 500);
  };

  const handleRemoveDomain = async (reason?: string) => {
    if (!id || !domainToRemove) return;

    try {
      const domain = domainToRemove.domain;
      const index = domainToRemove.index;
      
      // Get zoneId for this domain
      let zoneId = domainZoneIds[domain.domain];
      
      // If zoneId not found in cache, try to fetch from website
      if (!zoneId && domain.website_id) {
        try {
          const website = await websiteApi.getById(domain.website_id);
          if (website?.cloudflare_zone_id) {
            zoneId = website.cloudflare_zone_id;
          }
        } catch (error) {
          console.error('Failed to fetch website for zoneId:', error);
        }
      }
      
      // Remove domain from spam link with reason
      await spamLinkApi.removeDomain(id, index, reason);
      messageApi.success('Domain removed successfully');
      
      // Delete redirect rule from Cloudflare
      if (domain.domain && zoneId) {
        try {
          console.log(`Deleting redirect rule for ${domain.domain} (zoneId: ${zoneId})`);
          await cloudflareApi.deleteRedirectRule(
            domain.domain,
            zoneId
          );
          console.log(`Successfully deleted redirect rule for ${domain.domain}`);
        } catch (error: any) {
          console.error(`Failed to delete redirect rule for ${domain.domain}:`, error);
          console.error('Error details:', error.response?.data || error.message);
          // Don't show error to user, just log it
        }
      } else if (domain.domain && !zoneId) {
        console.warn(`No zoneId found for domain ${domain.domain}, skipping redirect rule deletion`);
      }
      
      // Update cloudflare_domain_status and status to 'active' if website_id exists
      if (domain.website_id) {
        try {
          await websiteApi.update(domain.website_id, {
            cloudflare_domain_status: 'active',
            status: 'active'
          } as any);
          console.log(`Updated website ${domain.website_id} status to 'active' after removal`);
        } catch (error: any) {
          console.error(`Failed to update status for website ${domain.website_id}:`, error);
        }
      }
      
      await loadSavedDomains();
      
      // Refresh history after removal
      refetchHistory();
      
      // Update target_domains_count after removal
      const updatedDomains = await spamLinkApi.getDomains(id);
      const actualCount = updatedDomains?.length || 0;
      if (spamLink && spamLink.target_domains_count !== actualCount) {
        try {
          await spamLinkApi.update(id, { target_domains_count: actualCount });
          console.log(`Updated target_domains_count from ${spamLink.target_domains_count} to ${actualCount}`);
          queryClient.invalidateQueries(['spam-link', id]);
        } catch (error: any) {
          console.error('Failed to update target_domains_count:', error);
        }
      }

      // Close modal and reset
      setRemoveModalVisible(false);
      setDomainToRemove(null);
      removeModalForm.resetFields();
    } catch (error: any) {
      messageApi.error('Failed to remove domain');
    }
  };

  const handleExportSavedDomainsCSV = () => {
    if (savedDomains.length === 0) {
      messageApi.warning('No domains to export');
      return;
    }

    // Prepare CSV data
    const headers = ['Domain', 'Domain URL', 'Status', 'Website ID', 'Zone ID'];
    const rows = savedDomains.map(domain => [
      domain.domain || '',
      domain.domain_url || '',
      domain.status || 'pending',
      domain.website_id || '',
      domainZoneIds[domain.domain] || '',
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const fileName = spamLink 
      ? `saved-domains-${spamLink.domain_url?.replace(/https?:\/\//, '').replace(/[^a-z0-9]/gi, '-')}-${new Date().toISOString().split('T')[0]}.csv`
      : `saved-domains-${new Date().toISOString().split('T')[0]}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    messageApi.success(`Exported ${savedDomains.length} domain(s) to CSV`);
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      if (id) {
        updateMutation.mutate({ id, data: { domain_url: values.domain_url } });
      }
    });
  };

  const handleSelectDomain = (website: Website) => {
    // Add to selected domains if not already selected
    if (!selectedDomains.includes(website.id)) {
      setSelectedDomains([...selectedDomains, website.id]);
    }
    messageApi.success(`Domain "${website.domain}" selected`);
  };

  const handleSaveDomains = async () => {
    if (!id || selectedDomains.length === 0) {
      messageApi.warning('Please select at least one domain');
      return;
    }

    setSavingDomains(true);
    try {
      // Get selected websites data
      const selectedWebsites = availableDomains.filter(w => selectedDomains.includes(w.id));
      
      // Filter out already saved domains (check by domain name)
      const savedDomainNames = new Set(savedDomains.map(d => d.domain));
      const newWebsites = selectedWebsites.filter(w => !savedDomainNames.has(w.domain));
      
      if (newWebsites.length === 0) {
        messageApi.warning('All selected domains are already saved');
        setSavingDomains(false);
        return;
      }

      const domainsToSave: AddDomainsDto = {
        domains: newWebsites.map(website => ({
          domain: website.domain,
          domain_url: website.url || `https://${website.domain}`,
          website_id: website.id,
        })),
      };

      console.log('Saving domains:', domainsToSave);
      const result = await spamLinkApi.addDomains(id, domainsToSave);
      console.log('Save result:', result);
      
      if (result && result.length > 0) {
        // Update status to "redirected" for all added websites
        // Note: cloudflare_domain_status will be synced with Cloudflare API separately
        console.log('Updating status to "redirected" for websites:', newWebsites.map(w => ({ id: w.id, domain: w.domain })));
        
        const updatePromises = newWebsites.map(async (website) => {
          try {
            const updated = await websiteApi.update(website.id, {
              status: 'redirected'
            } as any);
            console.log(`Successfully updated website ${website.id}:`, updated);
            return { success: true, websiteId: website.id };
          } catch (error: any) {
            console.error(`Failed to update status for website ${website.id} (${website.domain}):`, error);
            console.error('Error details:', error.response?.data || error.message);
            return { success: false, websiteId: website.id, error };
          }
        });
        
        const updateResults = await Promise.all(updatePromises);
        const successCount = updateResults.filter(r => r.success).length;
        const failedCount = updateResults.filter(r => !r.success).length;
        
        if (failedCount > 0) {
          console.warn(`${failedCount} website(s) failed to update status`);
        }

        // Create redirect rules for all added domains
        if (spamLink && spamLink.domain_url) {
          console.log('Creating redirect rules for domains:', newWebsites.map(w => ({ id: w.id, domain: w.domain })));
          
          // Show progress modal
          setRedirectProgress({
            visible: true,
            current: 0,
            total: newWebsites.length,
            currentDomain: '',
          });

          const redirectResults: Array<{ success: boolean; domain: string; error?: any }> = [];
          
          // Process sequentially to update progress
          for (let i = 0; i < newWebsites.length; i++) {
            const website = newWebsites[i];
            setRedirectProgress({
              visible: true,
              current: i,
              total: newWebsites.length,
              currentDomain: website.domain,
            });

            try {
              console.log(`Creating redirect rule for ${website.domain} to ${spamLink.domain_url}`);
              await cloudflareApi.createRedirectRule(
                website.domain,
                spamLink.domain_url,
                website.cloudflare_zone_id
              );
              console.log(`Successfully created redirect rule for ${website.domain}`);
              redirectResults.push({ success: true, domain: website.domain });
            } catch (error: any) {
              console.error(`Failed to create redirect rule for ${website.domain}:`, error);
              console.error('Error details:', error.response?.data || error.message);
              redirectResults.push({ success: false, domain: website.domain, error });
            }
          }

          // Update final progress
          setRedirectProgress({
            visible: true,
            current: newWebsites.length,
            total: newWebsites.length,
            currentDomain: '',
          });

          const redirectSuccessCount = redirectResults.filter(r => r.success).length;
          const redirectFailedCount = redirectResults.filter(r => !r.success).length;

          // Close progress modal after a short delay
          setTimeout(() => {
            setRedirectProgress({
              visible: false,
              current: 0,
              total: 0,
              currentDomain: '',
            });

            if (redirectFailedCount > 0) {
              console.warn(`${redirectFailedCount} domain(s) failed to create redirect rule`);
              messageApi.warning(`${redirectSuccessCount} redirect rule(s) created, ${redirectFailedCount} failed`);
            } else {
              messageApi.success(`Successfully created ${redirectSuccessCount} redirect rule(s)`);
            }
          }, 500);
        }
        
        messageApi.success(`Successfully saved ${result.length} domain(s) to database. Updated status for ${successCount} website(s).`);
        setSelectedDomains([]);
        await loadSavedDomains();
        
        // Refresh history after adding domains
        refetchHistory();
        
        // Update target_domains_count to match actual saved domains count
        const updatedDomains = await spamLinkApi.getDomains(id);
        const actualCount = updatedDomains?.length || 0;
        if (spamLink && spamLink.target_domains_count !== actualCount) {
          try {
            await spamLinkApi.update(id, { target_domains_count: actualCount });
            console.log(`Updated target_domains_count from ${spamLink.target_domains_count} to ${actualCount}`);
            queryClient.invalidateQueries(['spam-link', id]);
          } catch (error: any) {
            console.error('Failed to update target_domains_count:', error);
          }
        }
      } else {
        messageApi.warning('No new domains were saved (may already exist)');
      }
    } catch (error: any) {
      console.error('Error saving domains:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save domains';
      messageApi.error(errorMessage);
    } finally {
      setSavingDomains(false);
    }
  };

  const handleLoadMoreDomains = async (count: number) => {
    if (!id) return;
    
    setLoadingLoadMore(true);
    try {
      // Fetch fresh saved domains first
      const currentSavedDomains = await spamLinkApi.getDomains(id);
      const savedDomainNames = new Set(currentSavedDomains.map((d: SpamLinkDomain) => d.domain));
      
      // Fetch more domains to ensure we get enough new (not redirected) domains
      // Fetch count * 3 to ensure we have enough new domains after filtering
      const fetchLimit = count * 3;
      let allNewDomains: Website[] = [];
      let page = 1;
      const maxPages = 10; // Limit to prevent infinite loop
      
      // Keep fetching until we have enough new domains or reach max pages
      while (allNewDomains.length < count && page <= maxPages) {
        const websites = await websiteApi.getAll({ 
          page, 
          limit: fetchLimit,
          status: 'active',
          search: '' 
        });
        
        if (websites.data.length === 0) {
          break; // No more domains available
        }
        
        // Filter out already saved domains (check by domain name)
        const newDomains = websites.data.filter((w: Website) => !savedDomainNames.has(w.domain));
        allNewDomains = [...allNewDomains, ...newDomains];
        
        // If we have enough new domains, break
        if (allNewDomains.length >= count) {
          break;
        }
        
        // If we got less than requested, might be last page
        if (websites.data.length < fetchLimit) {
          break;
        }
        
        page++;
      }
      
      // Limit to the requested count
      const limitedDomains = allNewDomains.slice(0, count);
      
      setLoadMoreDomains(limitedDomains);
      setSelectedLoadMoreDomains([]);
      
      // Update target_domains_count: current value + number of available domains loaded
      if (limitedDomains.length > 0 && spamLink) {
        const newCount = (spamLink.target_domains_count || 0) + limitedDomains.length;
        try {
          await spamLinkApi.update(id!, { target_domains_count: newCount });
          console.log(`Updated target_domains_count from ${spamLink.target_domains_count} to ${newCount} (added ${limitedDomains.length} available domains)`);
          queryClient.invalidateQueries(['spam-link', id]);
        } catch (error: any) {
          console.error('Failed to update target_domains_count:', error);
        }
      }
      
      if (limitedDomains.length === 0) {
        messageApi.warning(`No new domains available to load. All available domains are already saved.`);
      } else {
        if (limitedDomains.length < count) {
          messageApi.warning(`Only found ${limitedDomains.length} new domain(s) out of ${count} requested.`);
        } else {
          messageApi.success(`Loaded ${limitedDomains.length} new domain(s) with status 'active'`);
        }
      }
    } catch (error: any) {
      console.error('Error loading more domains:', error);
      messageApi.error('Failed to load domains');
    } finally {
      setLoadingLoadMore(false);
    }
  };

  const handleSaveLoadMoreDomains = async () => {
    if (!id || selectedLoadMoreDomains.length === 0) {
      messageApi.warning('Please select at least one domain');
      return;
    }

    setSavingDomains(true);
    try {
      // Get selected websites data
      const selectedWebsites = loadMoreDomains.filter(w => selectedLoadMoreDomains.includes(w.id));
      
      // Filter out already saved domains (check by domain name)
      const savedDomainNames = new Set(savedDomains.map(d => d.domain));
      const newWebsites = selectedWebsites.filter(w => !savedDomainNames.has(w.domain));
      
      if (newWebsites.length === 0) {
        messageApi.warning('All selected domains are already saved');
        setSavingDomains(false);
        return;
      }

      const domainsToSave: AddDomainsDto = {
        domains: newWebsites.map(website => ({
          domain: website.domain,
          domain_url: website.url || `https://${website.domain}`,
          website_id: website.id,
        })),
      };

      const result = await spamLinkApi.addDomains(id, domainsToSave);
      
      if (result && result.length > 0) {
        // Update status to "redirected" for all added websites
        // Note: cloudflare_domain_status will be synced with Cloudflare API separately
        console.log('Updating status to "redirected" for websites:', newWebsites.map(w => ({ id: w.id, domain: w.domain })));
        
        const updatePromises = newWebsites.map(async (website) => {
          try {
            const updated = await websiteApi.update(website.id, {
              status: 'redirected'
            } as any);
            console.log(`Successfully updated website ${website.id}:`, updated);
            return { success: true, websiteId: website.id };
          } catch (error: any) {
            console.error(`Failed to update status for website ${website.id} (${website.domain}):`, error);
            console.error('Error details:', error.response?.data || error.message);
            return { success: false, websiteId: website.id, error };
          }
        });
        
        const updateResults = await Promise.all(updatePromises);
        const successCount = updateResults.filter(r => r.success).length;
        const failedCount = updateResults.filter(r => !r.success).length;
        
        if (failedCount > 0) {
          console.warn(`${failedCount} website(s) failed to update status`);
        }

        // Create redirect rules for all added domains
        if (spamLink && spamLink.domain_url) {
          console.log('Creating redirect rules for domains:', newWebsites.map(w => ({ id: w.id, domain: w.domain })));
          
          // Show progress modal
          setRedirectProgress({
            visible: true,
            current: 0,
            total: newWebsites.length,
            currentDomain: '',
          });

          const redirectResults: Array<{ success: boolean; domain: string; error?: any }> = [];
          
          // Process sequentially to update progress
          for (let i = 0; i < newWebsites.length; i++) {
            const website = newWebsites[i];
            setRedirectProgress({
              visible: true,
              current: i,
              total: newWebsites.length,
              currentDomain: website.domain,
            });

            try {
              console.log(`Creating redirect rule for ${website.domain} to ${spamLink.domain_url}`);
              await cloudflareApi.createRedirectRule(
                website.domain,
                spamLink.domain_url,
                website.cloudflare_zone_id
              );
              console.log(`Successfully created redirect rule for ${website.domain}`);
              redirectResults.push({ success: true, domain: website.domain });
            } catch (error: any) {
              console.error(`Failed to create redirect rule for ${website.domain}:`, error);
              console.error('Error details:', error.response?.data || error.message);
              redirectResults.push({ success: false, domain: website.domain, error });
            }
          }

          // Update final progress
          setRedirectProgress({
            visible: true,
            current: newWebsites.length,
            total: newWebsites.length,
            currentDomain: '',
          });

          const redirectSuccessCount = redirectResults.filter(r => r.success).length;
          const redirectFailedCount = redirectResults.filter(r => !r.success).length;

          // Close progress modal after a short delay
          setTimeout(() => {
            setRedirectProgress({
              visible: false,
              current: 0,
              total: 0,
              currentDomain: '',
            });

            if (redirectFailedCount > 0) {
              console.warn(`${redirectFailedCount} domain(s) failed to create redirect rule`);
              messageApi.warning(`${redirectSuccessCount} redirect rule(s) created, ${redirectFailedCount} failed`);
            } else {
              messageApi.success(`Successfully created ${redirectSuccessCount} redirect rule(s)`);
            }
          }, 500);
        }
        
        messageApi.success(`Successfully saved ${result.length} domain(s) to database. Updated status for ${successCount} website(s).`);
        setSelectedLoadMoreDomains([]);
        setLoadMoreDomains([]);
        setLoadMoreModalVisible(false);
        loadMoreForm.resetFields();
        await loadSavedDomains();
        
        // Refresh history after adding domains
        refetchHistory();
        
        // Update target_domains_count to match actual saved domains count
        const updatedDomains = await spamLinkApi.getDomains(id);
        const actualCount = updatedDomains?.length || 0;
        if (spamLink && spamLink.target_domains_count !== actualCount) {
          try {
            await spamLinkApi.update(id, { target_domains_count: actualCount });
            console.log(`Updated target_domains_count from ${spamLink.target_domains_count} to ${actualCount}`);
            queryClient.invalidateQueries(['spam-link', id]);
          } catch (error: any) {
            console.error('Failed to update target_domains_count:', error);
          }
        }
      } else {
        messageApi.warning('No new domains were saved (may already exist)');
      }
    } catch (error: any) {
      console.error('Error saving load more domains:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save domains';
      messageApi.error(errorMessage);
    } finally {
      setSavingDomains(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!spamLink) {
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/spam-links')} style={{ marginBottom: 16 }}>
          Back to Spam Links
        </Button>
        <Card>
          <p>Spam link not found</p>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/spam-links')} style={{ marginBottom: 16 }}>
          Back to Spam Links
        </Button>
        <Title level={2}>Edit Spam Link</Title>
        {spamLink && (
          <div style={{ marginTop: 8, fontSize: 16, color: '#666' }}>
            Total Domains to Redirect: <strong style={{ color: '#1890ff' }}>{spamLink.target_domains_count || 0}</strong>
          </div>
        )}
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            domain_url: spamLink.domain_url,
          }}
        >
          <Form.Item
            name="domain_url"
            label="Domain URL"
            rules={[
              { required: true, message: 'Please enter domain URL' },
              { type: 'url', message: 'Please enter a valid URL' },
            ]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={handleGetAvailableDomains}
                loading={loadingDomains}
              >
                Get Domains Available
              </Button>
              <Button type="primary" htmlType="submit" loading={updateMutation.isLoading}>
                Save Domain URL
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card
        title="Search Domain"
        style={{ marginTop: 24 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="Search domain by name or domain..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearchDomains}
              style={{ flex: 1 }}
              allowClear
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearchDomains}
              loading={loadingSearch}
            >
              Search
            </Button>
          </Space.Compact>
        </div>

        {loadingSearch && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        )}

        {!loadingSearch && searchResults.length > 0 && (
          <div>
            <div style={{ marginBottom: 8, color: '#666' }}>
              Found {searchResults.length} domain(s) matching "{searchText}"
            </div>
            <List
              dataSource={searchResults}
              renderItem={(website) => {
                const isInAvailable = availableDomains.some(d => d.id === website.id);
                const isSaved = savedDomains.some(d => d.website_id === website.id && d.domain === website.domain);
                
                return (
                  <List.Item
                    actions={[
                      <Button
                        type="primary"
                        onClick={() => handleAddDomainFromSearch(website)}
                        disabled={isInAvailable || isSaved}
                      >
                        {isInAvailable ? 'Already Added' : isSaved ? 'Already Saved' : 'Add to List'}
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <span>{website.name}</span>
                          <Tag color="blue">{website.domain}</Tag>
                          <CopyOutlined
                            style={{ fontSize: 18, cursor: 'pointer', color: '#1890ff' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(website.domain);
                              messageApi.success(`Domain "${website.domain}" copied to clipboard!`);
                            }}
                            title="Copy domain"
                          />
                          {isInAvailable && <Tag color="green">Added</Tag>}
                          {isSaved && <Tag color="orange">Saved</Tag>}
                        </Space>
                      }
                      description={
                        <div>
                          <div>URL: {website.url || `https://${website.domain}`}</div>
                          {website.description && (
                            <div style={{ marginTop: 4, color: '#999' }}>{website.description}</div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </div>
        )}

        {!loadingSearch && searchText && searchResults.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            No domains found matching "{searchText}"
          </div>
        )}

        {!loadingSearch && !searchText && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            Enter a domain name to search
          </div>
        )}
      </Card>

      {availableDomains.length > 0 && (
        <Card
          title={
            <Space>
              <span>Available Domains ({availableDomains.length})</span>
              {selectedDomains.length > 0 && (
                <Button
                  type="primary"
                  size="small"
                  onClick={handleSaveDomains}
                  loading={savingDomains}
                >
                  Save {selectedDomains.length} Selected Domain(s) to Database
                </Button>
              )}
            </Space>
          }
          style={{ marginTop: 24 }}
        >
          <List
            dataSource={availableDomains}
            renderItem={(website) => {
              const isSelected = selectedDomains.includes(website.id);
              const isSaved = savedDomains.some(d => d.website_id === website.id && d.domain === website.domain);
              
              return (
                <List.Item
                  actions={[
                    <Button
                      type={isSelected ? 'primary' : 'default'}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedDomains(selectedDomains.filter(id => id !== website.id));
                        } else {
                          handleSelectDomain(website);
                        }
                      }}
                      disabled={isSaved}
                    >
                      {isSaved ? 'Already Saved' : isSelected ? 'Selected' : 'Select'}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <span>{website.name}</span>
                        <Tag color="blue">{website.domain}</Tag>
                        <CopyOutlined
                          style={{ fontSize: 18, cursor: 'pointer', color: '#1890ff' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(website.domain);
                            messageApi.success(`Domain "${website.domain}" copied to clipboard!`);
                          }}
                          title="Copy domain"
                        />
                        {isSelected && <Tag color="green">Selected</Tag>}
                        {isSaved && <Tag color="orange">Saved</Tag>}
                      </Space>
                    }
                    description={
                      <div>
                        <div>URL: {website.url || `https://${website.domain}`}</div>
                        {website.description && (
                          <div style={{ marginTop: 4, color: '#999' }}>{website.description}</div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </Card>
      )}

      {savedDomains.length > 0 && (
        <Card
          title={`Saved Domains (${savedDomains.length})`}
          style={{ marginTop: 24 }}
          extra={
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setLoadMoreModalVisible(true)}
              >
                Load More Domains
              </Button>
              <Button
                type="default"
                icon={<RedoOutlined />}
                onClick={handleRebuildRedirectRules}
                loading={redirectProgress.visible}
                disabled={!spamLink || savedDomains.length === 0}
              >
                Rebuild Redirect Rule
              </Button>
              <Button
                type="default"
                icon={<DownloadOutlined />}
                onClick={handleExportSavedDomainsCSV}
                disabled={savedDomains.length === 0}
              >
                Export CSV
              </Button>
              <Button
                type="link"
                onClick={loadSavedDomains}
                loading={loadingSavedDomains}
              >
                Refresh
              </Button>
            </Space>
          }
        >
          <List
            loading={loadingSavedDomains}
            dataSource={savedDomains.map((domain, index) => ({ ...domain, index }))}
            renderItem={(item) => {
              const domain = item;
              const index = (item as any).index;
              return (
                <List.Item
                  actions={[
                    <Button
                      type="link"
                      danger
                      onClick={() => {
                        setDomainToRemove({ domain, index });
                        removeModalForm.resetFields();
                        setRemoveModalVisible(true);
                      }}
                    >
                      Remove
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <span>{domain.domain}</span>
                        <Tag color={domain.status === 'active' ? 'green' : 'orange'}>
                          {domain.status || 'pending'}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <div>URL: {domain.domain_url || `https://${domain.domain}`}</div>
                        {domain.website_id && (
                          <div style={{ marginTop: 4, fontSize: '12px', color: '#999' }}>
                            Website ID: {domain.website_id}
                          </div>
                        )}
                        {domainZoneIds[domain.domain] && (
                          <div style={{ marginTop: 4, fontSize: '12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: '#999' }}>Zone ID: </span>
                            <span 
                              style={{ 
                                fontFamily: 'monospace', 
                                color: '#1890ff'
                              }}
                            >
                              {domainZoneIds[domain.domain]}
                            </span>
                            <CopyOutlined 
                              style={{ 
                                color: '#1890ff',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                              onClick={() => {
                                navigator.clipboard.writeText(domainZoneIds[domain.domain]);
                                messageApi.success('Zone ID copied to clipboard!');
                              }}
                              title="Copy Zone ID"
                            />
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </Card>
      )}

      {/* Domain History Card */}
      <Card
        title={
          <Space>
            <HistoryOutlined />
            <span>Domain Usage History</span>
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => refetchHistory()}
              loading={loadingHistory}
            >
              Refresh
            </Button>
          </Space>
        }
        style={{ marginTop: 24 }}
      >
        {loadingHistory ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        ) : !domainHistory || domainHistory.length === 0 ? (
          <Empty description="No history records found" />
        ) : (
          <Table
            dataSource={domainHistory}
            rowKey="_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} records`,
            }}
            columns={[
              {
                title: 'Domain',
                dataIndex: 'domain',
                key: 'domain',
                render: (text: string, record: SpamLinkDomainHistory) => (
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      <Space>
                        <span>{text}</span>
                        <CopyOutlined
                          style={{ fontSize: 14, cursor: 'pointer', color: '#1890ff' }}
                          onClick={() => {
                            navigator.clipboard.writeText(text);
                            messageApi.success(`Domain "${text}" copied to clipboard!`);
                          }}
                          title="Copy domain"
                        />
                      </Space>
                    </div>
                    <div>
                      <Tag
                        color={
                          record.status === 'active'
                            ? 'green'
                            : record.status === 'recovered'
                            ? 'orange'
                            : 'default'
                        }
                        icon={
                          record.status === 'active' ? (
                            <CheckCircleOutlined />
                          ) : record.status === 'recovered' ? (
                            <CloseCircleOutlined />
                          ) : null
                        }
                      >
                        {record.status?.toUpperCase() || 'N/A'}
                      </Tag>
                    </div>
                  </div>
                ),
              },
              {
                title: 'Start Time',
                dataIndex: 'startTime',
                key: 'startTime',
                width: 180,
                render: (date: string) =>
                  date ? new Date(date).toLocaleString() : 'N/A',
                sorter: (a: SpamLinkDomainHistory, b: SpamLinkDomainHistory) =>
                  new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
              },
              {
                title: 'End Time',
                dataIndex: 'endTime',
                key: 'endTime',
                width: 180,
                render: (date: string) =>
                  date ? new Date(date).toLocaleString() : (
                    <span style={{ color: '#999' }}>Still Active</span>
                  ),
                sorter: (a: SpamLinkDomainHistory, b: SpamLinkDomainHistory) => {
                  if (!a.endTime && !b.endTime) return 0;
                  if (!a.endTime) return 1;
                  if (!b.endTime) return -1;
                  return new Date(a.endTime).getTime() - new Date(b.endTime).getTime();
                },
              },
              {
                title: 'Recovered At',
                dataIndex: 'recoveredAt',
                key: 'recoveredAt',
                width: 180,
                render: (date: string) =>
                  date ? new Date(date).toLocaleString() : 'N/A',
              },
              {
                title: 'Reason',
                dataIndex: 'reason',
                key: 'reason',
                ellipsis: true,
                render: (reason: string) =>
                  reason ? (
                    <span title={reason}>{reason}</span>
                  ) : (
                    <span style={{ color: '#999' }}>N/A</span>
                  ),
              },
            ]}
          />
        )}
      </Card>

      <Modal
        title="Load More Domains"
        open={loadMoreModalVisible}
        onOk={async () => {
          try {
            const values = await loadMoreForm.validateFields();
            await handleLoadMoreDomains(values.count);
          } catch (error) {
            console.error('Validation failed:', error);
          }
        }}
        onCancel={() => {
          setLoadMoreModalVisible(false);
          loadMoreForm.resetFields();
          setLoadMoreDomains([]);
          setSelectedLoadMoreDomains([]);
        }}
        width={800}
        okText="Load Domains"
        cancelText="Cancel"
        confirmLoading={loadingLoadMore}
      >
        <Form
          form={loadMoreForm}
          layout="vertical"
          initialValues={{ count: 10 }}
        >
          <Form.Item
            name="count"
            label="Number of Domains to Load"
            rules={[
              { required: true, message: 'Please enter number of domains' },
              { type: 'number', min: 1, max: 1000, message: 'Must be between 1 and 1000' },
            ]}
          >
            <InputNumber
              min={1}
              max={1000}
              style={{ width: '100%' }}
              placeholder="Enter number of domains"
            />
          </Form.Item>
        </Form>

        {loadMoreDomains.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                <strong>{selectedLoadMoreDomains.length}</strong> of <strong>{loadMoreDomains.length}</strong> domains selected
              </span>
              <Space>
                <Button
                  size="small"
                  onClick={() => {
                    const allIds = loadMoreDomains.map(d => d.id);
                    setSelectedLoadMoreDomains(
                      selectedLoadMoreDomains.length === loadMoreDomains.length ? [] : allIds
                    );
                  }}
                >
                  {selectedLoadMoreDomains.length === loadMoreDomains.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={handleSaveLoadMoreDomains}
                  disabled={selectedLoadMoreDomains.length === 0}
                  loading={savingDomains}
                >
                  Save Selected ({selectedLoadMoreDomains.length})
                </Button>
              </Space>
            </div>
            <List
              dataSource={loadMoreDomains}
              renderItem={(website) => {
                const isSelected = selectedLoadMoreDomains.includes(website.id);
                const isSaved = savedDomains.some(d => d.website_id === website.id && d.domain === website.domain);
                
                return (
                  <List.Item
                    actions={[
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLoadMoreDomains([...selectedLoadMoreDomains, website.id]);
                          } else {
                            setSelectedLoadMoreDomains(selectedLoadMoreDomains.filter(id => id !== website.id));
                          }
                        }}
                        disabled={isSaved}
                      >
                        {isSaved ? 'Already Saved' : 'Select'}
                      </Checkbox>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <span>{website.name}</span>
                          <Tag color="blue">{website.domain}</Tag>
                          {isSelected && <Tag color="green">Selected</Tag>}
                          {isSaved && <Tag color="orange">Already Saved</Tag>}
                        </Space>
                      }
                      description={
                        <div>
                          <div>URL: {website.url || `https://${website.domain}`}</div>
                          {website.description && (
                            <div style={{ marginTop: 4, color: '#999' }}>{website.description}</div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </div>
        )}
      </Modal>


      {/* Remove Domain Modal */}
      <Modal
        title="Remove Domain"
        open={removeModalVisible}
        onOk={async () => {
          try {
            const values = await removeModalForm.validateFields();
            await handleRemoveDomain(values.reason);
          } catch (error) {
            // Validation failed or remove failed
          }
        }}
        onCancel={() => {
          setRemoveModalVisible(false);
          setDomainToRemove(null);
          removeModalForm.resetFields();
        }}
        okText="Confirm Remove"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <p>
            Are you sure you want to remove domain <strong>{domainToRemove?.domain.domain}</strong>?
          </p>
        </div>
        <Form
          form={removeModalForm}
          layout="vertical"
        >
          <Form.Item
            name="reason"
            label="Reason (Optional)"
            help="Enter the reason for removing this domain. This will be saved in the domain history."
          >
            <Input.TextArea
              rows={4}
              placeholder="e.g., Domain no longer needed, Domain expired, etc."
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Redirect Progress Modal */}
      <Modal
        title="Creating Redirect Rules"
        open={redirectProgress.visible}
        footer={null}
        closable={false}
        maskClosable={false}
        width={500}
      >
        <div style={{ padding: '20px 0' }}>
          <Progress
            percent={redirectProgress.total > 0 ? Math.round((redirectProgress.current / redirectProgress.total) * 100) : 0}
            status={redirectProgress.current === redirectProgress.total && redirectProgress.total > 0 ? 'success' : 'active'}
            format={(percent) => `${redirectProgress.current} / ${redirectProgress.total}`}
          />
          {redirectProgress.currentDomain && (
            <div style={{ marginTop: 16, textAlign: 'center', color: '#666' }}>
              Processing: <strong>{redirectProgress.currentDomain}</strong>
            </div>
          )}
          {redirectProgress.current === redirectProgress.total && redirectProgress.total > 0 && (
            <div style={{ marginTop: 16, textAlign: 'center', color: '#52c41a' }}>
              <strong>Completed!</strong>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default SpamLinkDetail;

